import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBriefcase, LuCheck, LuLoaderCircle, LuRotateCcw, LuSave, LuTrash2 } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { Field, SelectInput, TextInput } from '../../../components/form/FormControls';
import { db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { DetailItem, HelpNote } from '../components/ProfileUi';
import { dateKey } from './calendarDates';
import { projectLabel } from './useCalendarData';

const EMPTY_JOB = { title: '', projectId: '', startDate: '', endDate: '', time: '' };

const JobIcon = () => (
  <GlassIcon gradient="tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30" size="xs">
    <LuBriefcase aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
  </GlassIcon>
);

/**
 * "Yeni İş Ekle": a personal to-do on the calendar (CalendarJobs). It can be
 * linked to one of the projects the employee is actively assigned to.
 */
export const AddJobDialog = ({ isOpen, initialDate, employeeId, projects, onClose, onCreated }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.jobForm.${key}`, vars);
  const [newJob, setNewJob] = useState(EMPTY_JOB);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const day = initialDate ? dateKey(initialDate) : '';
    setNewJob({ ...EMPTY_JOB, startDate: day, endDate: day });
  }, [isOpen, initialDate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { projectId, ...job } = newJob;
      const project = projects.find((item) => item.id === projectId);
      const jobData = {
        ...job,
        // Proje adı da yazılır: proje listesine erişmeyen ekranlar (raporlar) da okuyabilsin
        ...(project ? { projectId: project.id, projectName: projectLabel(project) } : {}),
        employeeId,
        createdAt: new Date().toISOString(),
        status: false,
      };
      const docRef = await addDoc(collection(db, 'CalendarJobs'), jobData);
      onCreated({ id: docRef.id, ...jobData });
      toast.success(label('added'));
      onClose();
    } catch (error) {
      console.error('Error adding job:', error);
      toast.error(label('addError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={<JobIcon />}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving} className="max-sm:tw-hidden">{t('profilePage.common.cancel')}</Button>
          <Button variant="primary" type="submit" form="profile-add-job" disabled={saving} className="max-sm:tw-flex-1">
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {t('profilePage.common.save')}
          </Button>
        </>
      )}
    >
      <form id="profile-add-job" onSubmit={handleSubmit} className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        <Field label={label('jobTitle')} htmlFor="job-title" required className="sm:tw-col-span-2">
          <TextInput
            id="job-title"
            data-autofocus
            required
            value={newJob.title}
            onChange={(event) => setNewJob({ ...newJob, title: event.target.value })}
            placeholder={label('jobTitlePlaceholder')}
          />
        </Field>
        <Field
          label={label('project')}
          htmlFor="job-project"
          hint={projects.length > 0 ? label('projectHint') : label('projectNone')}
          className="sm:tw-col-span-2"
        >
          <SelectInput
            id="job-project"
            value={newJob.projectId}
            disabled={projects.length === 0}
            onChange={(event) => setNewJob({ ...newJob, projectId: event.target.value })}
            placeholder={label('noProject')}
            options={projects.map((project) => ({ value: project.id, label: projectLabel(project) }))}
          />
        </Field>
        <Field label={label('startDate')} htmlFor="job-start" required>
          <TextInput
            id="job-start"
            type="date"
            required
            value={newJob.startDate}
            onChange={(event) => setNewJob({ ...newJob, startDate: event.target.value, endDate: newJob.endDate && newJob.endDate < event.target.value ? event.target.value : newJob.endDate })}
          />
        </Field>
        <Field label={label('endDate')} htmlFor="job-end" required hint={label('endDateHint')}>
          <TextInput
            id="job-end"
            type="date"
            required
            min={newJob.startDate || undefined}
            value={newJob.endDate}
            onChange={(event) => setNewJob({ ...newJob, endDate: event.target.value })}
          />
        </Field>
        <Field label={label('time')} htmlFor="job-time" hint={label('timeHint')}>
          <TextInput id="job-time" type="time" value={newJob.time} onChange={(event) => setNewJob({ ...newJob, time: event.target.value })} />
        </Field>
        <HelpNote className="sm:tw-col-span-2">{label('help')}</HelpNote>
      </form>
    </Modal>
  );
};

AddJobDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialDate: PropTypes.instanceOf(Date),
  employeeId: PropTypes.string,
  // Çalışanın aktif atandığı projeler (Pasif / Ayrıldı olanlar hariç)
  projects: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

AddJobDialog.defaultProps = { projects: [] };

/** "İş Detayları": mark done / not done, or delete. */
export const JobDetailDialog = ({ job, projectName, onClose, onUpdated, onDeleted }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.jobDetail.${key}`, vars);
  const [updating, setUpdating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeletingJob, setIsDeletingJob] = useState(false);

  const formatDay = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleToggleJobStatus = async () => {
    if (!job?.id) return;
    setUpdating(true);
    try {
      const newStatus = !job.status;
      await updateDoc(doc(db, 'CalendarJobs', job.id), {
        status: newStatus,
        lastUpdated: new Date().toISOString(),
      });
      onUpdated({ ...job, status: newStatus });
      toast.success(newStatus ? label('markedDone') : label('markedOpen'));
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error(label('updateError'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!job?.id) return;
    setIsDeletingJob(true);
    try {
      await deleteDoc(doc(db, 'CalendarJobs', job.id));
      onDeleted(job.id);
      toast.success(label('deleted'));
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error(label('deleteError'));
    } finally {
      setIsDeletingJob(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={Boolean(job)}
        onClose={() => !updating && onClose()}
        size="md"
        title={job?.title || label('title')}
        subtitle={label('title')}
        closeLabel={t('common.close')}
        lang={language}
        icon={<JobIcon />}
        footer={job && (
          <>
            <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={updating} className="tw-mr-auto">
              <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
              <span className="max-sm:tw-sr-only">{label('delete')}</span>
            </Button>
            <Button variant={job.status ? 'secondary' : 'success'} onClick={handleToggleJobStatus} disabled={updating} className="max-sm:tw-flex-1">
              {updating && <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />}
              {!updating && (job.status ? <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />)}
              {job.status ? label('markOpen') : label('markDone')}
            </Button>
          </>
        )}
      >
        {job && (
          <dl className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <DetailItem label={label('status')}>
              <Badge tone={job.status ? 'success' : 'warning'}>{t(`profilePage.calendar.jobStatus.${job.status ? 'done' : 'ongoing'}`)}</Badge>
            </DetailItem>
            {job.time && <DetailItem label={label('time')}>{job.time}</DetailItem>}
            {job.projectId && <DetailItem label={label('project')} wide>{projectName || job.projectName || '—'}</DetailItem>}
            <DetailItem label={label('startDate')}>{formatDay(job.startDate)}</DetailItem>
            <DetailItem label={label('endDate')}>{formatDay(job.endDate)}</DetailItem>
          </dl>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={label('deleteTitle')}
        message={label('deleteMessage', { title: job?.title || '' })}
        icon={LuTrash2}
        tone="danger"
        confirmLabel={label('delete')}
        cancelLabel={t('profilePage.common.cancel')}
        busy={isDeletingJob}
        onConfirm={handleDeleteJob}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

JobDetailDialog.propTypes = {
  job: PropTypes.object,
  // Bağlı projenin güncel adı (yoksa işte kayıtlı ad gösterilir)
  projectName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};
